// @flow
import * as React from "react";
import { Field, reduxForm, formValues, getFormValues, type FormProps } from "redux-form";
import { Form } from "semantic-ui-react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import classnames from "classnames";

import SaveButton from "./SaveButton";
import Section from "./Section";
import PaymentFormOptions from "Commons/Components/FormComponents/components/PaymentForm/Options";
import { sagaConnector } from "Commons/Utilities/redux";
import {
  DIRECT_DEBIT_IBAN,
  DIRECT_DEBIT_BLZ_ACCOUNT_NUMBER,
  DIFFERENT_ACCOUNT_OWNER,
} from "Commons/Components/FormComponents/components/PaymentForm/Constants";
import { AddressForm } from "./Customer/DeliveryAddress";
import { InputField, RadioGroupField } from "./Fields";
import { isRequired } from "../misc/Validators";
import { isIban, isBankCode, isGermanAccountNumber } from "Commons/Utilities/validators";
import FormLabel from "Commons/Components/FormComponents/components/FormLabelWithDivider";
import type { ChangeServiceT } from "modules/ChangeService/misc/Types";
import { formatIban, normaliseIban } from "../misc/helpers";

type MethodProps = {|
  t: typeof TFunction,
  desiredPaymentMethod: typeof DIRECT_DEBIT_IBAN | typeof DIRECT_DEBIT_BLZ_ACCOUNT_NUMBER,
  validation: {|
    isAccountBankNumberValid?: boolean,
    isAccountNumberValid?: boolean,
    isIbanValid?: boolean,
  |},
  isOptional?: boolean,
  showWarning?: boolean,
  className: string,
  width: number,
  noLabel: boolean,
|};

let MethodSection = ({
  t,
  desiredPaymentMethod,
  showWarning,
  isOptional = false,
  className,
  width = 10,
  noLabel,
  validation: { isAccountBankNumberValid, isAccountNumberValid, isIbanValid },
}: MethodProps) => {
  const isAccountBankNumberValidExternally = () =>
    isAccountBankNumberValid === false ? t("validation:bankCodeIsInvalid") : undefined;

  const isAccountNumberValidExternally = () =>
    isAccountNumberValid === false ? t("validation:accountNumberIsInvalid") : undefined;

  const isIbanValidExternally = () => (isIbanValid === false ? t("validation:ibanIsInvalid") : undefined);

  if (desiredPaymentMethod === DIRECT_DEBIT_IBAN) {
    return (
      <>
        <Form.Group data-cy="iban" className={classnames(className)}>
          {!noLabel && <FormLabel divider id="accountIban" title={t("common:payment.accountIban")} />}
          <Form.Field width={width}>
            <Field
              name="accountIban"
              validate={isOptional ? [isIban] : [isRequired, isIban, isIbanValidExternally]}
              component={InputField}
              format={formatIban}
              normalize={normaliseIban}
              props={{
                showWarning,
                id: "accountIban",
                placeholder: t("common:payment.accountIban"),
                normalizeOnBlur: (value) => value.replace(/\s/g, ""),
              }}
            />
          </Form.Field>
        </Form.Group>
      </>
    );
  }

  if (desiredPaymentMethod === DIRECT_DEBIT_BLZ_ACCOUNT_NUMBER) {
    return (
      <>
        <Form.Group data-cy="bankCode" className={classnames(className)}>
          {!noLabel && <FormLabel divider id="accountBankNumber" title={t("common:payment.bankCode")} />}
          <Form.Field width={width}>
            <Field
              name="accountBankNumber"
              validate={isOptional ? [] : [isRequired, isAccountBankNumberValidExternally, ...isBankCode]}
              component={InputField}
              props={{
                showWarning,
                id: "accountBankNumber",
                placeholder: t("common:payment.bankCode"),
              }}
            />
          </Form.Field>
        </Form.Group>
        <Form.Group data-cy="accountNumber">
          {!noLabel && <FormLabel divider id="accountNumber" title={t("common:payment.accountNumber")} />}
          <Form.Field width={width}>
            <Field
              name="accountNumber"
              validate={isOptional ? [] : [isRequired, isAccountNumberValidExternally, ...isGermanAccountNumber]}
              component={InputField}
              props={{
                showWarning,
                id: "accountNumber",
                placeholder: t("common:payment.accountNumber"),
              }}
            />
          </Form.Field>
        </Form.Group>
      </>
    );
  }

  return null;
};

export const Method = formValues("desiredPaymentMethod")(MethodSection);

const addressFields = {
  salutation: "differentAccountHolderSalutation",
  salutationTitle: "differentAccountHolderSalutationTitle",
  firstName: "differentAccountHolderFirstname",
  lastName: "differentAccountHolderLastname",
  companyName: "differentAccountHolderCompanyName",
  street: "differentAccountHolderStreet",
  streetNumber: "differentAccountHolderStreetNumber",
  postalCode: "differentAccountHolderPostalcode",
  locationId: "differentAccountHolderLocationId",
  locationMainName: "differentAccountHolderLocationMainName",
  locationName: "differentAccountHolderLocation",
  locationPartName: "differentAccountHolderLocationPartName",
};

type AccountOwnerProps = {|
  t: typeof TFunction,
  initialValues: ChangeServiceT,
  currentValues: ChangeServiceT,
  accountOwnerType: typeof DIFFERENT_ACCOUNT_OWNER,
  form: string,
  change: $PropertyType<FormProps, "change">,
|};

let AccountOwnerSection = (props: AccountOwnerProps) => {
  const { t, initialValues, accountOwnerType, form, change, currentValues } = props;

  if (accountOwnerType === DIFFERENT_ACCOUNT_OWNER) {
    return (
      <AddressForm
        fields={addressFields}
        initialValues={initialValues}
        currentValues={currentValues}
        t={t}
        form={form}
        change={change}
        dataCy="differentAccountOwner"
      />
    );
  }
  return null;
};

const AccountOwner = formValues("accountOwnerType")(AccountOwnerSection);

type PaymentProps = {|
  t: typeof TFunction,
  initialValues: ChangeServiceT,
  hideSaveButton?: boolean,
|} & FormProps;

let Payment = (props: PaymentProps) => {
  const {
    t,
    initialValues = {},
    invalid,
    submitFailed,
    submitting,
    submitSucceeded,
    change,
    handleSubmit,
    form,
    hideSaveButton = false,
  } = props;

  const currentValues = useSelector((state) => getFormValues("ChangeService")(state));

  return (
    <Section title={t("wizard.payment.name")} id="bankdaten">
      <Form.Group>
        <FormLabel divider id="desiredPaymentMethod" title={t("common:payment.paymentMethod.questionLabel")} />
        <Field
          name="desiredPaymentMethod"
          component={RadioGroupField}
          props={{
            options: PaymentFormOptions.paymentMethod,
            onChange: () => {
              change("accountIban", null);
              change("accountBankNumber", null);
              change("accountNumber", null);
            },
          }}
        />
      </Form.Group>
      <Method t={t} showWarning />
      <Form.Group>
        <FormLabel divider id="accountOwnerType" title={t("common:payment.accountOwnerType.label")} />
        <Field
          name="accountOwnerType"
          component={RadioGroupField}
          props={{
            options: PaymentFormOptions.accountOwnerType,
          }}
        />
      </Form.Group>
      <AccountOwner t={t} currentValues={currentValues} initialValues={initialValues} form={form} change={change} />
      {!hideSaveButton && (
        <SaveButton
          invalid={invalid}
          submitting={submitting}
          error={submitFailed}
          submitSucceeded={submitSucceeded}
          onClick={handleSubmit}
        />
      )}
    </Section>
  );
};

Payment = withTranslation("changeService")(Payment);

export const PaymentForm = reduxForm({
  onSubmit: (values, dispatch) => {
    return sagaConnector(
      "UPDATE_CHANGE_SERVICE_REQUESTED",
      "UPDATE_CHANGE_SERVICE_SUCCEEDED",
      "UPDATE_CHANGE_SERVICE_FAILED",
      {
        changeService: values,
        userId: values.userId,
        id: values.id,
        categoryId: values.categoryId,
      },
      dispatch
    );
  },
})(Payment);

export default Payment;
